// Tab "Einstellungen" (E1-E19 of APP-PARITY.md, the machine only since 15.10.2026): on & off with standby, keeping
// warm, warm-up flush, weekly plan and holiday; scale; water tank; display; advanced; system. Every catalog value gets
// a control descriptor (stepper, switch, choice, text) from the catalog: unit, step, limits, options, rights.
import { findCommand } from '@brewcore/protocol';
import type { CommandInput } from '../commands.js';
import { t, type TextKey } from '../i18n.js';
import {
  dayFromIso,
  dayText,
  hm,
  isoOfDay,
  localDayOf,
  nextSwitchOn,
  onFor,
  parsePlan,
  planText,
  PLAN_WINDOWS,
  readyOf,
  type PlanWindow,
  type WeekPlan,
} from '../schedule.js';
import type { ParamValue } from '../store.js';
import { liveOf, opOf } from '../status.js';
import { actionView, type ActionView, type ScreenContext } from './context.js';
import { decimalsOf, formatNumber } from './format.js';

// ------------------------------------------------------------------------------------------------ controls

export interface ControlView {
  id: string;
  kind: 'stepper' | 'switch' | 'choice' | 'text';
  label: string;
  /** a short hint under the label, one line ("Bezüge", "Wirkt nach einem Neustart") */
  hint: string | null;
  /**
   * the explanation behind an (i) after the label (boards: no hint line next to a stepper; 23.10.2026 the hints broke
   * over three lines there)
   */
  info: string | null;
  /** protection S or an owner command while the user is a member: shown once per page, not at every row (ownerNote) */
  ownerOnly: boolean;
  value: ParamValue | undefined;
  /** the value as shown ("93,5 °C", "aus") */
  text: string;
  /** a number as a typed value starts ("93,5", no unit; D-G04), null for anything else */
  editText: string | null;
  unit: string | null;
  decimals: number;
  min: number | null;
  max: number | null;
  step: number;
  /** from 0 ("aus", "nie") the first + starts here (ZERO_START of the local app) */
  zeroStart: number | null;
  options: { value: number; label: string; on: boolean }[];
  maxLength: number | null;
  editable: boolean;
  /** shown provisionally (sent, not yet confirmed by the machine) */
  provisional: boolean;
  /** takes effect after a restart (catalog reboot) */
  afterRestart: boolean;
}

export interface ControlOptions {
  label?: TextKey;
  hint?: TextKey;
  /** the explanation behind the (i) */
  info?: TextKey;
  /** label without translation (Kp, Tn, …) */
  rawLabel?: string;
  unit?: string;
  zeroText?: TextKey;
  zeroStart?: number;
  /** whole numbers without ",0" ("10 s", "93 °C"; boards "Display", "Rezept"), halves still with it */
  whole?: boolean;
  /** only these enum values */
  only?: number[];
}

/** A catalog value as a control; null if the machine type does not know it. */
export function controlView(ctx: ScreenContext, id: string, o: ControlOptions = {}): ControlView | null {
  const c = findCommand(ctx.catalog, id);
  if (!c || c.kind !== 'set') return null;
  const value = ctx.value(id);
  const decimals = decimalsOf(c.step);
  const unit = o.unit ?? c.unit ?? null;
  const kind: ControlView['kind'] =
    c.type === 'bool' ? 'switch' : c.type === 'enum' ? 'choice' : c.type === 'string' ? 'text' : 'stepper';
  const options = (c.options ?? [])
    .filter((x) => !o.only || o.only.includes(x.value))
    .map((x) => ({ value: x.value, label: x.label[ctx.lang], on: x.value === value }));
  let text = '–';
  if (typeof value === 'number')
    text =
      kind === 'choice'
        ? (options.find((x) => x.on)?.label ?? String(value))
        : value === 0 && o.zeroText
          ? ctx.tr(o.zeroText)
          : `${formatNumber(value, o.whole && Number.isInteger(value) ? 0 : decimals, ctx.lang)}${unit ? `\u00a0${unit}` : ''}`;
  else if (typeof value === 'boolean') text = ctx.tr(value ? 'on' : 'off');
  else if (typeof value === 'string') text = value;
  const owner = (c.protection === 'S' || c.role === 'owner') && !ctx.isOwner;
  return {
    id,
    kind,
    label: o.rawLabel ?? (o.label ? ctx.tr(o.label) : c.label[ctx.lang]),
    hint: o.hint ? ctx.tr(o.hint) : null,
    info: o.info ? ctx.tr(o.info) : null,
    ownerOnly: owner,
    value,
    text,
    editText:
      kind === 'stepper' && typeof value === 'number' ? formatNumber(value, decimals, ctx.lang) : null,
    unit,
    decimals,
    min: c.min ?? null,
    max: c.max ?? null,
    step: c.step ?? 1,
    zeroStart: o.zeroStart ?? null,
    options,
    maxLength: c.maxLength ?? null,
    editable: ctx.canEdit(id),
    provisional: ctx.view.provisional.has(id),
    afterRestart: c.reboot === true,
  };
}

/**
 * "Nur der Besitzer kann das ändern." once for a page whose values a member can't change (23.10.2026: the sentence
 * at every row made every hint wrap); null for the owner or when every value is open.
 */
export function ownerNote(
  ctx: ScreenContext,
  controls: readonly (ControlView | null | undefined)[],
): string | null {
  return controls.some((c) => c?.ownerOnly) ? ctx.tr('ownerOnlyNote') : null;
}

/** A typed number: as precise as the control shows, NaN for anything that is no number. */
export function parseTyped(text: string, decimals: number): number {
  const n = Number.parseFloat(text.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 10 ** decimals) / 10 ** decimals : Number.NaN;
}

/** A switch or choice as one set. */
export const setValue = (id: string, value: ParamValue): CommandInput => ({
  kind: 'set',
  params: { [id]: value },
});

// ------------------------------------------------------------------------------------------------ on & off

export interface PowerView {
  title: string;
  standby: ControlView | null;
  standbyTime: ControlView | null;
  warmTemp: ControlView | null;
  warmHours: ControlView | null;
  displayOff: ControlView | null;
  warmupFlush: ControlView | null;
  warmupWait: ControlView | null;
  schedule: ControlView | null;
  plan: PlanView | null;
  holiday: HolidayView | null;
}

const DISPLAY_OFF = [0, 10, 30, 60];

export function powerView(ctx: ScreenContext): PowerView {
  const standby = ctx.value('standby.enabled') === true;
  const warm = ctx.num('standby.warm_temp') > 0;
  const schedule = ctx.value('schedule.enabled') === true;
  const displayOff = controlView(ctx, 'standby.display_off', { label: 'displayOff', hint: 'displayOffHint' });
  if (displayOff) {
    const v = ctx.num('standby.display_off');
    const choices = DISPLAY_OFF.includes(v) ? DISPLAY_OFF : [...DISPLAY_OFF, v].sort((a, b) => a - b);
    displayOff.kind = 'choice';
    displayOff.options = choices.map((x) => ({
      value: x,
      label: x === 0 ? ctx.tr('displayAlways') : `${x} min`,
      on: x === v,
    }));
  }
  return {
    title: ctx.tr('sPower'),
    standby: controlView(ctx, 'standby.enabled', { label: 'standbyEnabled', hint: 'standbyEnabledHint' }),
    standbyTime: standby ? controlView(ctx, 'standby.time', { label: 'standbyTime', unit: 'min' }) : null,
    warmTemp: controlView(ctx, 'standby.warm_temp', {
      label: 'warmTemp',
      zeroText: 'zeroOff',
      zeroStart: 70,
    }),
    warmHours: warm ? controlView(ctx, 'standby.warm_hours', { label: 'warmHours', unit: 'h' }) : null,
    displayOff,
    warmupFlush: ctx.watertank
      ? controlView(ctx, 'brew.warmup_flush', { label: 'warmupFlush', hint: 'warmupFlushHint' })
      : null,
    warmupWait:
      ctx.watertank && ctx.value('brew.warmup_flush') === true
        ? controlView(ctx, 'brew.warmup_flush_wait', {
            label: 'warmupWait',
            hint: 'warmupWaitHint',
            unit: 'min',
          })
        : null,
    schedule: controlView(ctx, 'schedule.enabled', { label: 'scheduleEnabled', hint: 'scheduleEnabledHint' }),
    plan: schedule ? planView(ctx) : null,
    holiday: schedule ? holidayView(ctx) : null,
  };
}

// ------------------------------------------------------------------------------------------------ weekly plan

export interface PlanSlot {
  /** the window this field opens: its own, or the day's next free one */
  window: number;
  /** "06:45–09:00"; null for a free field */
  text: string | null;
  /**
   * a free field after the next free one: the machine keeps a day's times as a list, so a day without a first time
   * cannot hold a second one; it shows "–" and takes no tap (the next free field shows "+")
   */
  inert: boolean;
  /** "Montag, Zeit 2": the sheet's title */
  title: string;
  /** "Montag, Zeit 2, 06:45–09:00" or "Freitag, Zeit 2 hinzufügen" */
  label: string;
  /** what the sheet starts with: the ready time, the off time ('' none) */
  ready: string;
  off: string;
}

export interface PlanView {
  /** "Zeiten in Europe/Berlin" when the browser is elsewhere */
  zoneHint: string | null;
  days: {
    index: number;
    name: string;
    summary: string;
    empty: boolean;
    windows: { on: string; off: string }[];
    /** the day's fields, Zeit 1 and Zeit 2 */
    slots: PlanSlot[];
  }[];
  editable: boolean;
}

/** "07:00–09:30" with the ready time (the plan's switch-on time plus the lead) */
const readyText = (w: PlanWindow, lead: number): string =>
  hm(readyOf(w.on, lead)) + (w.off !== null && w.off !== w.on ? `–${hm(w.off)}` : '');

const minutesOf = (hhmm: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  return m && Number(m[1]) < 24 && Number(m[2]) < 60 ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** a new first time is ready at 07:00 without an off time, a new second one 17:00–20:00 (planBox() of the local app) */
const NEW_WINDOW = [
  { ready: '07:00', off: '' },
  { ready: '17:00', off: '20:00' },
];

/** where the off wheel starts when the sheet turns an off time on: two hours after the ready time it shows then */
export const planOffFor = (ready: string): string => hm(((minutesOf(ready) ?? 0) + 120) % 1440);

/** The week as the page shows it: ready times (switch-on plus the lead, core/schedule.ts), off times as they are. */
export function planView(ctx: ScreenContext, browserZone?: string): PlanView {
  const plan = parsePlan(ctx.value('schedule.plan'));
  const names = ctx.tr('dayNames').split(',');
  const long = ctx.tr('dayNamesLong').split(',');
  const here = browserZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return {
    zoneHint: ctx.zone && ctx.zone !== here ? ctx.tr('zoneHint', { z: ctx.zone }) : null,
    days: names.map((name, index) => {
      const day = plan[index] ?? [];
      const windows = day.map((w) => ({
        on: hm(readyOf(w.on, ctx.lead)),
        off: w.off !== null && w.off !== w.on ? hm(w.off) : '',
      }));
      const d = long[index] ?? name;
      const slots = Array.from({ length: PLAN_WINDOWS }, (_, k): PlanSlot => {
        const w = windows[k];
        const title = ctx.tr('planSlotTitle', { d, n: Math.min(k, day.length) + 1 });
        if (w) {
          const text = w.off ? `${w.on}–${w.off}` : w.on;
          return {
            window: k,
            text,
            inert: false,
            title,
            label: `${title}, ${text}`,
            ready: w.on,
            off: w.off,
          };
        }
        const fresh = NEW_WINDOW[day.length] ?? { ready: '07:00', off: '' };
        return {
          window: day.length,
          text: null,
          inert: k > day.length,
          title,
          label: ctx.tr('planSlotAdd', { d, n: day.length + 1 }),
          ...fresh,
        };
      });
      return {
        index,
        name,
        summary: day.length ? day.map((w) => readyText(w, ctx.lead)).join(' · ') : ctx.tr('planNone'),
        empty: day.length === 0,
        windows,
        slots,
      };
    }),
    editable: ctx.canEdit('schedule.plan'),
  };
}

export type PlanEdit =
  /**
   * one time of a day as the sheet holds it: the ready time and the off time ('' none); window is its place or the
   * day's next free one; all: the same time on every day, in the same place where a day has it, else after its times
   */
  | { op: 'set'; day: number; window: number; ready: string; off: string; all?: boolean }
  | { op: 'remove'; day: number; window: number };

/** One edit of the weekly plan as the whole schedule.plan in one set; null if the edit changes nothing. */
export function planEdit(ctx: ScreenContext, e: PlanEdit): CommandInput | null {
  const plan = parsePlan(ctx.value('schedule.plan'));
  const day = plan[e.day];
  if (!day) return null;
  let next: WeekPlan;
  switch (e.op) {
    case 'set': {
      const ready = minutesOf(e.ready);
      const off = e.off ? minutesOf(e.off) : null;
      if (ready === null || (e.off && off === null) || e.window > day.length || e.window >= PLAN_WINDOWS)
        return null;
      const w: PlanWindow = { on: onFor(ready, ctx.lead), off };
      const put = (d: PlanWindow[]): PlanWindow[] =>
        e.window < d.length
          ? d.map((x, y) => (y === e.window ? w : x))
          : d.length < PLAN_WINDOWS
            ? [...d, w]
            : d;
      next = plan.map((d, x) => (e.all || x === e.day ? put(d) : d));
      break;
    }
    case 'remove':
      next = plan.map((d, x) => (x === e.day ? d.filter((_, y) => y !== e.window) : d));
      break;
  }
  const text = planText(next);
  return text === planText(plan) ? null : setValue('schedule.plan', text);
}

// ------------------------------------------------------------------------------------------------ holiday

export interface HolidayView {
  label: string;
  hint: string;
  /** "pausiert bis 24.10." while it lasts (a row's summary), else null */
  until: string | null;
  /** the same as the first words of a line ("Pausiert bis 24.10.", the holiday's sheet) */
  untilTitle: string | null;
  end: CommandInput | null;
  /** earliest day for the date picker */
  min: string;
  /** what the date field starts with: tomorrow (23.10.2026: iOS showed an empty field as a bare dark bar) */
  start: string;
  editable: boolean;
}

export function holidayView(ctx: ScreenContext): HolidayView {
  const until = ctx.num('schedule.pause_until');
  const active = until >= ctx.today;
  return {
    label: ctx.tr('holiday'),
    hint: ctx.tr('holidayHint'),
    until: active ? ctx.tr('holidayUntil', { d: dayText(until, ctx.lang) }) : null,
    untilTitle: active ? ctx.tr('pausedUntil', { d: dayText(until, ctx.lang) }) : null,
    end: active ? setValue('schedule.pause_until', 0) : null,
    min: isoOfDay(ctx.today),
    start: isoOfDay(ctx.today + 1),
    editable: ctx.canEdit('schedule.pause_until'),
  };
}

/** Holiday through a day from the date picker (the machine's local day); null for an invalid or past day. */
export function holidayCommand(ctx: ScreenContext, iso: string): CommandInput | null {
  const day = dayFromIso(iso);
  return day === null || day < ctx.today ? null : setValue('schedule.pause_until', day);
}

// ------------------------------------------------------------------------------------------------ scale

export interface ScaleView {
  title: string;
  enabled: ControlView | null;
  autoTare: ControlView | null;
  learn: ControlView | null;
  status: { text: string; connected: boolean };
  /** hint and search while switched on and no scale is chosen */
  search: { hint: string; find: ActionView; searching: boolean } | null;
  found: { address: string; name: string; signal: string; connect: ActionView }[];
  noneFound: string | null;
  forget: ActionView | null;
}

interface ScaleGroup {
  status?: 'off' | 'none' | 'disconnected' | 'connected';
  selected?: { address: string; name: string } | null;
  battery?: number | null;
  searching?: boolean;
  found?: { address: string; name: string; rssi: number }[];
}

/** Scale (E8-E12): choose one by searching, connect, forget; only the chosen scale is connected. */
export function scaleView(ctx: ScreenContext, searched: boolean): ScaleView {
  const s = (ctx.view.groups.scale?.data ?? {}) as ScaleGroup;
  const byWeight = ctx.value('brew.mode') === 1 && ctx.value('brew.by_weight.enabled') === true;
  const on = s.status !== undefined && s.status !== 'off';
  const signal = (rssi: number): string =>
    ctx.tr(rssi >= -65 ? 'signalGood' : rssi >= -80 ? 'signalOk' : 'signalWeak');
  const found = on && !s.selected ? (s.found ?? []) : [];
  return {
    title: ctx.tr('sScale'),
    enabled: controlView(ctx, 'hardware.sensors.scale.enabled', {
      label: 'scaleEnabled',
      info: 'scaleEnabledInfo',
    }),
    autoTare: byWeight
      ? controlView(ctx, 'brew.by_weight.auto_tare', { label: 'autoTare', hint: 'autoTareHint' })
      : null,
    learn: controlView(ctx, 'brew.by_weight.learn', { label: 'learnLag', hint: 'learnLagHint' }),
    status: {
      connected: s.status === 'connected',
      text:
        s.status === 'connected' && s.selected
          ? `${ctx.tr('scaleWith', { n: s.selected.name })}${typeof s.battery === 'number' ? ` · ${ctx.tr('battery')} ${s.battery} %` : ''}`
          : s.status === 'disconnected' && s.selected
            ? ctx.tr('scaleWaiting', { n: s.selected.name })
            : s.status === 'off'
              ? ctx.tr('scaleDisabled')
              : ctx.tr('scaleNoneChosen'),
    },
    search:
      on && !s.selected
        ? {
            hint: ctx.tr('scaleHint'),
            find: actionView(ctx, 'scale.discover', {
              label: s.searching ? 'scaleSearching' : 'scaleFind',
              when: !s.searching,
            }),
            searching: s.searching === true,
          }
        : null,
    found: found.map((f) => ({
      address: f.address,
      name: f.name,
      signal: signal(f.rssi),
      connect: actionView(ctx, 'scale.select', {
        label: 'scaleConnect',
        args: { address: f.address, name: f.name },
      }),
    })),
    noneFound:
      on && !s.selected && searched && !s.searching && found.length === 0 ? ctx.tr('scaleNoneFound') : null,
    forget: on && s.selected ? actionView(ctx, 'scale.forget', { label: 'scaleForget' }) : null,
  };
}

// ------------------------------------------------------------------------------------------------ the other cards

export interface SettingsCards {
  tank: {
    title: string;
    enabled: ControlView | null;
    mode: ControlView | null;
    /** board "Wassertank": "Tank jetzt · Wasser da" while the sensor is on and the machine reports its state */
    now: { label: string; text: string; empty: boolean } | null;
  };
  display: {
    title: string;
    language: ControlView | null;
    postBrew: ControlView | null;
    /** the word the round display shows after a shot in its language ("FERTIG", "DONE") */
    doneWord: string;
  };
  /** board "Fortgeschritten": the heating in one card, the PID values in another */
  advanced: { title: string; heating: ControlView[]; pid: ControlView[]; controls: ControlView[] };
  system: { title: string; controls: ControlView[] };
}

const present = (xs: (ControlView | null)[]): ControlView[] => xs.filter((x): x is ControlView => x !== null);

export function settingsCards(ctx: ScreenContext): SettingsCards {
  const tank = ctx.value('hardware.sensors.watertank.enabled') === true;
  const language = controlView(ctx, 'display.language', { label: 'displayLanguage' });
  // the round display speaks German and English
  if (language) language.options = language.options.filter((x) => x.value === 0 || x.value === 1);
  const doneWord = t(ctx.value('display.language') === 1 ? 'en' : 'de', 'dialDone');
  const postBrew = controlView(ctx, 'display.post_brew_timer_duration', {
    info: 'postBrewInfo',
    unit: 's',
    whole: true,
  });
  if (postBrew) postBrew.label = ctx.tr('postBrew', { w: doneWord });
  const state = liveOf(ctx.view, ctx.now)?.state ?? opOf(ctx.view).state;
  const heating = present([
    // PID on/off for maintenance and tuning only; in daily use standby turns the heater off (19.10.2026)
    controlView(ctx, 'pid.enabled', { label: 'pidEnabled', info: 'pidEnabledInfo' }),
    controlView(ctx, 'brew.temp_offset', { label: 'tempOffset', info: 'tempOffsetInfo' }),
    controlView(ctx, 'display.blinking.delta', { label: 'readyBand', info: 'readyBandInfo' }),
    controlView(ctx, 'brew.heat_boost', { label: 'heatBoost', info: 'heatBoostInfo', zeroText: 'zeroOff' }),
  ]);
  const pid = present([
    controlView(ctx, 'pid.regular.kp', { rawLabel: 'Kp' }),
    controlView(ctx, 'pid.regular.tn', { rawLabel: 'Tn' }),
    controlView(ctx, 'pid.regular.tv', { rawLabel: 'Tv' }),
    controlView(ctx, 'pid.regular.i_max', { rawLabel: 'I-Max' }),
    controlView(ctx, 'pid.ema_factor', { label: 'emaHint', info: 'emaInfo' }),
    controlView(ctx, 'pid.use_ponm', { rawLabel: 'PonM', info: 'ponmInfo' }),
  ]);
  return {
    tank: {
      title: ctx.tr('sTank'),
      enabled: controlView(ctx, 'hardware.sensors.watertank.enabled', {
        label: 'tankSensor',
        info: 'tankSensorInfo',
      }),
      mode: tank
        ? controlView(ctx, 'hardware.sensors.watertank.mode', { label: 'tankMode', info: 'tankModeInfo' })
        : null,
      now:
        tank && ctx.controllable && state !== undefined
          ? {
              label: ctx.tr('tankNow'),
              text: ctx.tr(state === 70 ? 'tankEmpty' : 'tankWater'),
              empty: state === 70,
            }
          : null,
    },
    display: { title: ctx.tr('sDisplay'), language, postBrew, doneWord },
    advanced: { title: ctx.tr('sPid'), heating, pid, controls: [...heating, ...pid] },
    system: {
      title: ctx.tr('sSystem'),
      controls: present([
        controlView(ctx, 'system.hostname', { label: 'hostname', info: 'hostnameInfo' }),
        controlView(ctx, 'system.log_level', { label: 'logLevel', info: 'logLevelInfo' }),
      ]),
    },
  };
}

// ------------------------------------------------------------------------------------------------ summaries

/**
 * The schedule in one line for the row of the main page (UX line 19.10.2026: the plan is a page of its own): when it
 * switches on, "Mo–Fr 06:45 · Sa, So 08:30", "pausiert bis 24.10.", "aus". Days in a row with the same switch-on times
 * are one range; the off times are on the page.
 */
export function scheduleSummary(ctx: ScreenContext, browserZone?: string): string {
  return scheduleSummaryParts(ctx, browserZone).join(' · ');
}

/**
 * The parts of scheduleSummary, in the order they matter: the row shows them in one line (FitLine), what does not fit
 * drops out from the end (23.10.2026: "Mo–Do 06:50 · Fr 06:50, 16:05 · Sa, So 08:35" wrapped).
 */
export function scheduleSummaryParts(ctx: ScreenContext, browserZone?: string): string[] {
  if (ctx.value('schedule.enabled') !== true) return [ctx.tr('off')];
  const holiday = holidayView(ctx);
  if (holiday.until) return [holiday.until];
  const days = planView(ctx, browserZone).days;
  const runs: { from: number; to: number; ons: string }[] = [];
  for (const d of days) {
    if (d.empty) continue;
    const ons = d.windows.map((w) => w.on).join(', ');
    const last = runs.at(-1);
    if (last && last.ons === ons && last.to === d.index - 1) last.to = d.index;
    else runs.push({ from: d.index, to: d.index, ons });
  }
  if (runs.length === 0) return [ctx.tr('planEmpty')];
  const name = (i: number) => days[i]?.name ?? '';
  return runs.map((r) => {
    const span =
      r.to - r.from >= 2
        ? `${name(r.from)}–${name(r.to)}`
        : r.to > r.from
          ? `${name(r.from)}, ${name(r.to)}`
          : name(r.from);
    return `${span} ${r.ons}`;
  });
}

/**
 * The head of the schedule's page (prototype "Zeitplan"): when it is ready on workdays, big, and the weekend in a line;
 * when the days differ, the next ready time. Below, when the next preheating starts; behind the (i) how long it takes
 * and what that is based on.
 */
export function scheduleHead(ctx: ScreenContext): {
  label: string;
  time: string;
  line: string | null;
  preheat: string | null;
  info: string;
  /** no time in the plan: one sentence instead of the time (WRITING.md 0a: no "–" for what is unknown) */
  empty: string | null;
} | null {
  if (ctx.value('schedule.enabled') !== true) return null;
  const plan = parsePlan(ctx.value('schedule.plan'));
  const first = (i: number) => {
    const w = plan[i]?.[0];
    return w ? hm(readyOf(w.on, ctx.lead)) : null;
  };
  const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
  const clock = (ms: number) =>
    new Date(ms).toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', timeZone: ctx.zone });
  const work = new Set([0, 1, 2, 3, 4].map(first));
  const weekend = new Set([5, 6].map(first));
  const one = (xs: Set<string | null>) => (xs.size === 1 ? [...xs][0] : undefined);
  const w = one(work);
  const e = one(weekend);
  const next = nextSwitchOn(plan, {
    enabled: true,
    pauseUntil: ctx.num('schedule.pause_until'),
    now: ctx.now,
    zone: ctx.zone,
  });
  const n = ctx.heatup?.samples ?? 0;
  const info =
    ctx.heatup?.seconds !== null && ctx.heatup?.seconds !== undefined && n > 0
      ? ctx.tr(n === 1 ? 'preheatInfoOne' : 'preheatInfo', { n: ctx.lead, k: n })
      : ctx.tr('preheatInfoNone', { n: ctx.lead });
  // only for today and tomorrow: further ahead the time says little
  const nextDays = next === null ? null : localDayOf(next, ctx.zone) - ctx.today;
  const preheat =
    next === null || nextDays === null || nextDays > 1
      ? null
      : ctx.tr(nextDays === 0 ? 'preheatToday' : 'preheatTomorrow', { t: clock(next) });
  if (w && e !== undefined)
    return {
      label: ctx.tr('schedWorkdays'),
      time: w,
      line: e ? ctx.tr('schedWeekendAt', { t: e }) : ctx.tr('schedWeekendNone'),
      preheat,
      info,
      empty: null,
    };
  if (next === null)
    return { label: '', time: '', line: null, preheat: null, info, empty: ctx.tr('schedEmpty') };
  const days = localDayOf(next, ctx.zone) - ctx.today;
  return {
    label: ctx.tr('schedNextLabel'),
    time: clock(next + ctx.lead * 60_000),
    preheat,
    info,
    empty: null,
    line:
      days === 0
        ? ctx.tr('today')
        : days === 1
          ? ctx.tr('tomorrow')
          : new Date(next).toLocaleDateString(loc, { weekday: 'long', timeZone: ctx.zone }),
  };
}
